import {
  Icon,
  Image,
  Input,
  Tooltip,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CONTACT_TYPE,
  GIFT_STATUSES,
  EDITABLE_TIMELINE_STATUS,
  SOURCE_TYPES,
  PRODUCT_TYPES,
  MAX_FIRST_NAME_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_LAST_NAME_LENGTH
} from '@hero/constants';
import { PageLayout } from '@hero/layouts';
import { DetailGroup, DetailItem, OrderStatusBadge } from '@hero/molecules';
import {
  useGetRecognitionRecipient,
  useUpdateRecognitionRecipient
} from '@hero/services/order-service';
import { getName, getSafeInnerHtml } from '@hero/utils';
import { getDiffDatesWithFormatted } from '@hero/utils/dateUtils';
import { formatCurrency, formatDate, getGiftStatus, parseContactName } from '@hero/utils/helper';
import { addNewContactSchema } from '@hero/validations';

import { EGiftButton } from './EGiftButton';
import * as S from './style';
import { TableHeaderButton } from './TableHeaderButton';

const GIFT_STATUS_LIST = [
  GIFT_STATUSES.emailSent,
  GIFT_STATUSES.emailOpened,
  GIFT_STATUSES.giftOpened,
  GIFT_STATUSES.giftRedeemed,
  GIFT_STATUSES.giftDelivered
];

function RecognitionRecipientDetail() {
  const { t } = useTranslation();
  const { orderId, recognitionCode } = useParams();
  const navigate = useNavigate();

  const { data: order } = useGetRecognitionRecipient({ orderId }, { enabled: !!orderId });
  const { mutateAsync: updateRecipient } = useUpdateRecognitionRecipient();

  const [fullNameInput, setFullNameInput] = useState('');

  const {
    status,
    giftStatus,
    recipient,
    recognition,
    deliveryInfo,
    merchantOrder,
    experience,
    selectedSku,
    timeline,
    link
  } = order || {};

  const isTeams = recognition?.source === SOURCE_TYPES.teams_app;

  const formik = useFormik({
    initialValues: {
      firstName: recipient?.firstName ?? '',
      lastName: recipient?.lastName ?? '',
      email: recipient?.email ?? ''
    },
    enableReinitialize: true,
    validationSchema: addNewContactSchema
  });

  useEffect(() => {
    setFullNameInput(`${formik.values.firstName} ${formik.values.lastName}`.trim());
  }, [formik.values.firstName, formik.values.lastName]);

  const _updateRecipientDetail = () => {
    updateRecipient({
      params: {
        orderCode: order.code
      },
      body: {
        recipient: {
          firstName: formik.values.firstName,
          lastName: formik.values.lastName,
          email: formik.values.email
        }
      }
    });
  };

  const giftStatusValue = React.useMemo(
    () => status && getGiftStatus({ status, giftStatus, deliveryDate: deliveryInfo?.deliveryDate }),
    [status, giftStatus, deliveryInfo]
  );

  const isEditable =
    timeline?.length === 0 ||
    (timeline?.some(t => EDITABLE_TIMELINE_STATUS.includes(t.action)) &&
      !timeline?.some(t => t.action !== GIFT_STATUSES.giftRedeemed));

  const hasTableHeader = EDITABLE_TIMELINE_STATUS.includes(giftStatus) && !isTeams;
  const hasEGift = selectedSku?.type === PRODUCT_TYPES.GIFT_CARD && !!order?.meta?.incommOrderUri;

  if (status) {
    status.deliveredAt = deliveryInfo?.deliveryDate;
  }

  const giftStatusLabel = (
    <S.GiftStatusGroup>
      <Typography variant="body1Bold">{t('giftStatus')}</Typography>
      <OrderStatusBadge status={giftStatusValue}></OrderStatusBadge>
    </S.GiftStatusGroup>
  );

  return (
    <PageLayout
      header={{
        title: t('featureRecipientDetails', { featureType: t('oneTimeGift') }),
        backButton: true
      }}
      navigation={{
        leftButton: {
          children: t('backToFeatureDetails', { featureType: t('oneTimeGift') }),
          onClick: () => navigate(-1)
        }
      }}
    >
      <S.RecipientDetailWrapper>
        <S.CardHeader>
          <Icon name="contacts" color="var(--color-accent-primary-default)" />
          <Typography variant="body1Bold">
            {getName({ firstName: recipient?.firstName, lastName: recipient?.lastName })}
          </Typography>
          {hasEGift && <EGiftButton code={order.code} email={recipient?.email} />}
          {hasTableHeader && <VerticalDivider $height="24px" $margin="0 0 0 16px" />}
          {hasTableHeader && (
            <TableHeaderButton link={link} recognitionCode={recognitionCode} orderId={orderId} />
          )}
        </S.CardHeader>
        <S.SectionWrapper>
          <S.ContactInfo>
            <S.Box>
              <DetailGroup title={t('recipientInformation')}>
                <DetailItem
                  title={
                    <>
                      {t('name')} (
                      <span style={{ textTransform: 'capitalize' }}>{t('optional')}</span>):
                    </>
                  }
                  editable={isEditable}
                  withBorder
                  saveDetail={_updateRecipientDetail}
                  isCTADisabled={!formik.isValid}
                  editInput={() => (
                    <Input
                      name="fullName"
                      label={`${t('name')}`}
                      variant="outlined"
                      size="sm"
                      value={fullNameInput}
                      onChange={e => {
                        setFullNameInput(e.target.value);
                      }}
                      onBlur={() => {
                        const { firstName, lastName } = parseContactName(fullNameInput);

                        formik.setFieldValue('firstName', firstName || '');
                        formik.setFieldValue('lastName', lastName || '');
                      }}
                      inputProps={{ maxLength: MAX_FIRST_NAME_LENGTH + MAX_LAST_NAME_LENGTH }}
                    />
                  )}
                >
                  <Typography variant="body2" style={{ paddingLeft: 13.8 }}>
                    {getName({
                      firstName: formik.values.firstName,
                      lastName: formik.values.lastName
                    })}
                  </Typography>
                </DetailItem>
                <DetailItem
                  title={`${t('email')}:`}
                  editable={isEditable}
                  saveDetail={_updateRecipientDetail}
                  isCTADisabled={!formik.isValid || !formik.values.email.trim()}
                  editInput={() => (
                    <Input
                      name="email"
                      label={t('email')}
                      variant="outlined"
                      size="sm"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
                    />
                  )}
                >
                  <Typography variant="body2" style={{ paddingLeft: 13.8 }}>
                    {recipient?.email}
                  </Typography>
                </DetailItem>
              </DetailGroup>
              <DetailGroup title={t('deliveryInformation')}>
                <DetailItem title={`${t('firstName')}:`} withBorder>
                  {deliveryInfo?.firstName ? (
                    deliveryInfo?.firstName
                  ) : (
                    <S.TextPlaceholder>
                      (
                      {t('giftRecipientNamePlaceholder', {
                        name: t('firstName')
                      })}
                      )
                    </S.TextPlaceholder>
                  )}
                </DetailItem>
                <DetailItem title={`${t('lastName')}:`} withBorder>
                  {deliveryInfo?.lastName ? (
                    deliveryInfo?.lastName
                  ) : (
                    <S.TextPlaceholder>
                      (
                      {t('giftRecipientNamePlaceholder', {
                        name: t('lastName')
                      })}
                      )
                    </S.TextPlaceholder>
                  )}
                </DetailItem>
                <DetailItem title={`${t('address')}:`}>
                  {deliveryInfo?.address?.addressLine1 ? (
                    <>
                      {`${deliveryInfo?.address?.city}, ${deliveryInfo?.address?.stateAbbv} ${deliveryInfo?.address?.postalCode}`}
                    </>
                  ) : (
                    <S.TextPlaceholder>
                      (
                      {t('giftRecipientNamePlaceholder', {
                        name: t('address')
                      })}
                      )
                    </S.TextPlaceholder>
                  )}
                </DetailItem>
                <Typography variant="caption1" emphasis="weaker">
                  *
                  <Trans
                    i18nKey="addressDescriptionText"
                    components={{
                      a: (
                        <a
                          target="_blank"
                          href={t('privacyPolicyUrl')}
                          rel="noreferrer"
                          style={{
                            color: 'var(--color-fg-weaker)',
                            textDecoration: 'underline'
                          }}
                        />
                      )
                    }}
                    values={{ name: deliveryInfo?.firstName || recipient?.firstName }}
                  />
                </Typography>
                {deliveryInfo?.deliveryDate && (
                  <DetailItem
                    title={`${t('deliveryDate')}:`}
                    withBorder={!!deliveryInfo?.merchantOrderId}
                  >
                    {formatDate(deliveryInfo?.deliveryDate, { includeTime: false })}
                  </DetailItem>
                )}
                {deliveryInfo?.merchantOrderId && !merchantOrder?.trackingCode && (
                  <DetailItem title={`${t('merchantOrderId')}:`}>
                    {deliveryInfo?.merchantOrderId}
                  </DetailItem>
                )}
                {merchantOrder?.trackingCode && (
                  <DetailItem title={`${t('trackingCode')}:`}>
                    {merchantOrder?.trackingCode}
                  </DetailItem>
                )}
                {merchantOrder?.trackingLink && (
                  <DetailItem title={`${t('trackingLink')}:`}>
                    {merchantOrder?.trackingLink}
                  </DetailItem>
                )}
              </DetailGroup>
            </S.Box>
          </S.ContactInfo>
          <S.GiftDetail>
            <S.Box isRight>
              <S.GiftStatus>
                <DetailGroup header={giftStatusLabel} className="detail-group">
                  {selectedSku?.type === 'charity' ? (
                    <S.RecipientCharity>
                      <S.RecipientCharityImage>
                        <S.RecipientCharityImageSource
                          src={selectedSku?.images[0].url}
                          alt=""
                          width={120}
                          height={130}
                        />
                        <S.IconWrapper>
                          <Tooltip content={t('charityIconTooltipContent')}>
                            <Icon name="f-heart" color="var(--color-error-default)" />
                          </Tooltip>
                        </S.IconWrapper>
                      </S.RecipientCharityImage>
                      <S.RecipientCharityBody>
                        <S.RecipientCharityTitle>{selectedSku?.name}</S.RecipientCharityTitle>
                        <S.RecipientCharityDescription>
                          <div {...getSafeInnerHtml(selectedSku?.shortDesc)} />
                        </S.RecipientCharityDescription>
                      </S.RecipientCharityBody>
                    </S.RecipientCharity>
                  ) : (
                    <S.ProductWrapper isNonEmpty={!!selectedSku}>
                      <S.ProductCard key={selectedSku?._id}>
                        <Image
                          src={
                            get(selectedSku, 'personalization.imageURL') ||
                            get(selectedSku, 'images[0].url')
                          }
                          alt=""
                          height={80}
                          width={80}
                          style={{
                            objectFit:
                              selectedSku?.meta?.productType === PRODUCT_TYPES.GIFT_CARD
                                ? 'contain'
                                : 'inherit'
                          }}
                        />
                        {selectedSku?.name ? (
                          <>
                            <S.ProductDetail>
                              <Typography variant="body2Bold" color="navy">
                                {selectedSku?.merchant?.name}
                              </Typography>
                              <S.ProductName>{selectedSku?.name}</S.ProductName>
                            </S.ProductDetail>
                            <Typography variant="body2Bold">
                              {formatCurrency(selectedSku.price)}
                            </Typography>
                          </>
                        ) : (
                          <S.TextPlaceholder>
                            {t('nameGiftSelectionWillShowHere', {
                              name: recipient?.firstName
                            })}
                          </S.TextPlaceholder>
                        )}
                      </S.ProductCard>
                    </S.ProductWrapper>
                  )}
                  {status?.accepted && (
                    <DetailItem title={`${t('regifted')}:`} className="regifted non-empty">
                      {t(deliveryInfo?.recipientType !== 'myself' ? 'yes' : 'no')}
                    </DetailItem>
                  )}
                  <DetailItem title={`${t('orderCode')}:`} withBorder>
                    {order?.code}
                  </DetailItem>
                  {recognition?.contacts?.type === CONTACT_TYPE.list && (
                    <DetailItem
                      title={`${t('acceptanceTime')}:`}
                      className={{
                        'non-empty': !isEditable
                      }}
                      withBorder
                    >
                      {!!status?.acceptedAt && !!status?.openedAt ? (
                        getDiffDatesWithFormatted({
                          start: status?.acceptedAt,
                          end: status?.openedAt
                        })
                      ) : (
                        <S.TextPlaceholder style={{ textAlign: 'right' }}>
                          0d, 0h, 0m, 0s
                        </S.TextPlaceholder>
                      )}
                    </DetailItem>
                  )}
                  <DetailItem title={`${t('name')}:`} withBorder>
                    {recognition?.name}
                  </DetailItem>
                  <DetailItem title={`${t('message')}:`} withBorder>
                    <div {...getSafeInnerHtml(experience?.giftMessage)} />
                  </DetailItem>
                  <DetailItem title={`${t('tyNote')}:`}>
                    {experience?.thankYouMessage ? (
                      <div {...getSafeInnerHtml(experience?.thankYouMessage)} />
                    ) : (
                      <S.TextPlaceholder>
                        ({t('tyNotePlaceholder', { name: recipient?.firstName })})
                      </S.TextPlaceholder>
                    )}
                  </DetailItem>
                </DetailGroup>
              </S.GiftStatus>
              <S.TimeLine>
                <S.Steps>
                  {timeline &&
                    GIFT_STATUS_LIST.map(giftStatus => {
                      const timeItem = timeline.find(time => time.action === giftStatus);
                      return (
                        <S.StepsContainer key={giftStatus} isSelected={timeItem}>
                          <S.Dot isSelected={timeItem} />
                          <S.StepContent>
                            <Typography
                              variant="body2Bold"
                              emphasis={timeItem ? 800 : 'weakest2'}
                              uppercase
                              style={{ width: 60 }}
                              color="fg"
                            >
                              {t(giftStatus)}
                            </Typography>
                            {timeItem && (
                              <S.StepDate>
                                <Icon name="calendar" size="sm" />
                                <div>
                                  <Typography variant="caption1Bold" style={{ opacity: 0.8 }}>
                                    {dayjs(timeItem?.date).tz('est').format('MM/DD/YYYY')}
                                  </Typography>
                                  <Typography variant="caption2Bold">
                                    {dayjs(timeItem?.date).tz('est').format('h:mm A')} ({t('est')})
                                  </Typography>
                                </div>
                              </S.StepDate>
                            )}
                          </S.StepContent>
                        </S.StepsContainer>
                      );
                    })}
                </S.Steps>
              </S.TimeLine>
            </S.Box>
          </S.GiftDetail>
        </S.SectionWrapper>
      </S.RecipientDetailWrapper>
    </PageLayout>
  );
}

export { RecognitionRecipientDetail };
